import { useMemo, useState } from "react";
import styled from "styled-components";
import Tab from "components/tab";
import Estimates from "./containers/estimates";
import Invoices from "./containers/invoices";
import Requests from "./containers/requests";
import CustomerFiles from "./containers/customerFiles";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import Spinner from "components/spinner";
import SafeArea from "components/safe-area";
import { theme } from "styles/theme";
import { Avatar } from "components/avatar";
import FlatContainer from "components/container/flat";
import { Button, OutlineButton } from "components/button";
import { ExtendedContact, TCustomer } from "types/customer";
import { useAreYouSure } from "components/areYouSure";
import { useSnackbarContext } from "components/snackbar";
import CustomerTagList from "./customer-tag-list";
import { TProperty } from "types/property";
import PropertyCon from "views/properties/propertyCon";
import NewPropertyModal from "views/properties/newPropertyModal";
import { RiAddLine, RiArrowDownSLine, RiBillLine, RiCloseLine, RiDeleteBin7Line } from "react-icons/ri";
import { IoNotifications } from "react-icons/io5";
import { useModalContext } from "components/modal";
import Dropdown from "components/dropdown";
import RequestModal from "views/requests/newRequestModal";
import CustomerPaymentsTab from "./containers/payments";
import NewPaymentModal from "views/payment/newPaymentModal";
import { dollarFormatter } from "utils";
import RequestNotes from "views/request/container/notes";
import ServicePlans from "../service-plans";
import Sync from "./containers/sync";
import CustomerCommunicationsTab from "./containers/communications";
import PaymentMethodsWrapper from "./containers/PaymentMethods";
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber } from "utils/phoneNumberFormatter";
import ContactInfoDisplay from "./component/ContactInfoDisplay";
import TooltippedElement from "components/TooltippedElement";

export default function Customer() {
  const { t } = useTranslation();
  const tBase = "views.customer.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const params = useParams<{ id: string }>(),
    history = useHistory(),
    { activate } = useAreYouSure(),
    { showSnackbar } = useSnackbarContext(),
    modal = useModalContext();

  const [showArchivedServicePlans, setShowArchivedServicePlans] = useState(false);
  const [showPrimaryExtras, setShowPrimaryExtras] = useState(false);

  const customer = useQuery<{
    info: TCustomer & { total_amount_due: number };
    counts: {
      estimates: string;
      invoices: string;
      requests: string;
      payments: string;
      notes: string;
      projects: string;
      files: string;
      payment_methods: string;
      service_plans: string;
      service_plans_archived: string;
      communications_emails?: string;
      communications_texts?: string;
    };
  }>(
    ["customer", params.id],
    async () =>
      await axios
        .get(
          `
        ${process.env.REACT_APP_SERVER_URL}/api/v2/company/customers/${params.id}
        `
        )
        .then((res) => {
          return res.data;
        })
  );

  // Outreach opt-out status (STOP replies / unsubscribe links). Businesses
  // without outreach access get a 403 here — swallow it and show nothing.
  const commPreferences = useQuery<{
    emailOptedOut: boolean;
    emailOptedOutAt: string | null;
    /** Employee who did it; null means the customer opted themselves out. */
    emailOptedOutBy: string | null;
    smsOptedOut: boolean;
    smsOptedOutAt: string | null;
    smsOptedOutBy: string | null;
    /**
     * Set when mail to this customer's address is being refused — a different
     * fact from an opt-out, and one that survives Resubscribe. Only delivery
     * failures appear here; an unsubscribe is already carried by the flags
     * above.
     */
    suppression: {
      email: string;
      reason: "bounce" | "complaint" | "manual";
      source: string | null;
      at: string | null;
    } | null;
  } | null>(
    ["communication-preferences", params.id],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/communications/preferences/${params.id}`
        )
        .then((res) => res.data?.data ?? null)
        .catch(() => null),
    { retry: false }
  );

  const resubscribe = useMutation(
    async (channel: "email" | "sms") =>
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/communications/preferences/${params.id}/resubscribe`,
        { channel }
      ),
    {
      onSuccess: () => {
        commPreferences.refetch();
        showSnackbar(tr("Resubscribed"));
      },
    }
  );

  /**
   * Clearing a delivery failure is deliberately its own action, not part of
   * Resubscribe. Resubscribe reverses a decision the customer made; this
   * corrects a fact about their mailbox, and the server lifts the address at
   * AWS as well as locally so the change actually takes effect.
   */
  const clearSuppression = useMutation(
    async () =>
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/communications/preferences/${params.id}/clear-suppression`
      ),
    {
      onSuccess: () => {
        commPreferences.refetch();
        showSnackbar(tr("Delivery failure cleared"));
      },
      onError: (error: any) => {
        showSnackbar(
          error?.response?.data?.error || tr("Could not clear the delivery failure"),
          "error"
        );
      },
    }
  );

  /**
   * Unsubscribe a customer who asked by phone (ESERV2-1465).
   *
   * The server records the acting employee from the session, so the ledger can
   * later distinguish a stop the customer made themselves from one staff made
   * on their behalf. Reversed by the existing Resubscribe, which is why this
   * writes the same preference rows rather than a suppression.
   */
  const optOut = useMutation(
    async (channel: "email" | "sms" | "all") =>
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/communications/preferences/${params.id}/opt-out`,
        { channel }
      ),
    {
      onSuccess: () => {
        commPreferences.refetch();
        showSnackbar(tr("Customer unsubscribed"));
      },
      onError: (error: any) =>
        showSnackbar(
          error?.response?.data?.error || tr("Could not unsubscribe the customer"),
          "error"
        ),
    }
  );

  const deleteCustomer = useMutation(
    async () =>
      await axios
        .delete(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${params.id}
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        showSnackbar(tr("Customer deleted successfully!"));
        history.replace("/customers");
      },
    }
  );

  if (customer.isLoading) return <Spinner />;

  const data = {
    overview: {
      tab: tr("Overview"),
      content: <CustomerInfoTab data={customer.data?.info} />,
    },
    requests: {
      tab: tr("Requests"),
      count: customer.data?.counts?.requests,
      content: <Requests id={params.id} />,
    },
    // projects: {
    //   tab: `Projects`,
    //   count: customer.data?.counts.projects,
    //   content: <Projects id={params.id} />,
    // },
    estimates: {
      tab: tr("Estimates"),
      count: customer.data?.counts?.estimates,
      content: <Estimates id={params.id} />,
    },
    servicePlans: {
      tab: tr("Service Plans"),
      count: showArchivedServicePlans ? (
        (Number(customer.data?.counts?.service_plans) ?? 0) + (Number(customer.data?.counts?.service_plans_archived) ?? 0)
      ) : customer.data?.counts?.service_plans,
      content: (
        <ServicePlans
          id={params.id}
          email={customer.data?.info.email || ""}
          setShowArchivedServicePlans={setShowArchivedServicePlans}
        />
      ),
    },
    invoices: {
      tab: tr("Invoices"),
      count: customer.data?.counts?.invoices,
      content: <Invoices id={params.id} />,
    },
    payments: {
      tab: tr("Payments"),
      count: customer.data?.counts?.payments,
      content: <CustomerPaymentsTab id={params.id} />,
    },
    notes: {
      tab: tr("Notes"),
      count: customer.data?.counts?.notes,
      content: <RequestNotes id={params.id} type="customer" />,
    },
    files: {
      tab: tr("Files"),
      count: customer.data?.counts?.files,
      content: <CustomerFiles id={params.id} type="customerFiles" />,
    },
    communications: {
      tab: tr("Communications"),
      count:
        (Number(customer.data?.counts?.communications_emails) || 0) +
          (Number(customer.data?.counts?.communications_texts) || 0) || undefined,
      content: (
        <CustomerCommunicationsTab
          id={params.id}
          emailCount={customer.data?.counts?.communications_emails}
          textCount={customer.data?.counts?.communications_texts}
        />
      ),
    },
    sync: {
      tab: tr("Sync"),
      content: <Sync id={params.id} />,
    },
    payment_methods: {
      tab: tr("Payment Methods"),
      count: customer.data?.counts?.payment_methods,
      content: <PaymentMethodsWrapper id={params.id} customer={customer.data?.info} />,
    },
  };

  const phoneSimplify = (phone: any) => {
    if (!phone) return null;
    // if (phone.length >= 14 && !ext) return null;
    if (typeof phone !== "string") {
      phone = String(phone);  // Convert to string if not already
    }
    return phone.replace(/[^0-9]/g, "");
  };

  const phoneNumberPre = phoneSimplify(customer.data?.info.phone);
  const phoneNumber = formatPhoneNumber(phoneNumberPre);

  const primaryExtraContactRows = (customer.data?.info.extended_contacts || []).filter(
    (e: ExtendedContact) =>
      !e.fk_business_customer_contact_id &&
      (e.contact_type === "email" || e.contact_type === "phone")
  );

  return (
    <div>
      <SafeArea
        style={{
          content: {
            padding: ".25rem 0",
          },
          overlay: { borderBottom: `1px solid ${theme.border.light}` },
        }}
      >
        <div
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", flexDirection: "row" }}>
              <Avatar
                showLabel
                size="xlarge"
                verified={customer.data?.info.fk_customer_id}
              >
                {customer.data?.info.name}
              </Avatar>
              <CustomerTagList tags={customer.data?.info} />
            </div>
            <span style={{ color: theme.color.subtext3, lineHeight: "2rem" }}>
              {customer.data?.info.email} {phoneNumber ? `• ${phoneNumber}` : ""}
              {customer.data?.info?.extension &&
                ` *${customer.data?.info?.extension}`}
              {customer.data?.info?.title && (
                <span> • {customer.data?.info?.title}</span>
              )}
              <span className="inline-flex items-center gap-1.5 ml-2">
                <span>•</span>
                <TooltippedElement
                  message={tr("Billing")}
                  placement="top"
                  element={
                    <RiBillLine
                      className={`text-lg ${
                        customer.data?.info?.is_billing
                          ? "text-emerald-600"
                          : "text-slate-200"
                      }`}
                      aria-label={tr("Billing")}
                    />
                  }
                />
                <TooltippedElement
                  message={tr("Notification")}
                  placement="top"
                  element={
                    <IoNotifications
                      className={`text-lg ${
                        customer.data?.info?.is_notification
                          ? "text-blue-500"
                          : "text-slate-200"
                      }`}
                      aria-label={tr("Notification")}
                    />
                  }
                />
              </span>
            </span>
            {primaryExtraContactRows.length > 0 && (
              <div
                className="block mt-1"
                style={{ color: theme.color.subtext3, lineHeight: "1.5rem" }}
              >
                <button
                  type="button"
                  className="text-blue-600 hover:underline"
                  onClick={() => setShowPrimaryExtras((v) => !v)}
                >
                  {`(+${primaryExtraContactRows.length}...)`}
                </button>
                {showPrimaryExtras && (
                  <div
                    className="absolute z-[100] mt-2 p-3 bg-white border border-slate-200 rounded shadow-lg min-w-[360px]"
                    style={{ top: "100%", left: 0 }}
                  >
                    <button
                      type="button"
                      className="absolute top-2 right-2 text-slate-500 hover:text-slate-700"
                      onClick={() => setShowPrimaryExtras(false)}
                      aria-label="Close"
                    >
                      <RiCloseLine />
                    </button>
                    <div className="pr-6">
                      <ContactInfoDisplay
                        entries={primaryExtraContactRows}
                        billing
                        notification
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            {commPreferences.data?.suppression && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs"
                  title={tr(
                    "Email to this address is being refused. Correcting the address also fixes this, since the block follows the address."
                  )}
                >
                  {commPreferences.data.suppression.reason === "complaint"
                    ? tr("Marked as spam")
                    : tr("Undeliverable")}
                  {commPreferences.data.suppression.at &&
                    ` — ${new Date(
                      commPreferences.data.suppression.at
                    ).toLocaleDateString()}`}
                  <button
                    type="button"
                    className="underline font-medium disabled:opacity-50"
                    disabled={clearSuppression.isLoading}
                    onClick={() => clearSuppression.mutate()}
                  >
                    {clearSuppression.isLoading ? tr("Clearing") : tr("Clear")}
                  </button>
                </span>
              </div>
            )}
            {(commPreferences.data?.smsOptedOut ||
              commPreferences.data?.emailOptedOut) && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {commPreferences.data?.smsOptedOut && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">
                    {commPreferences.data?.smsOptedOutBy
                      ? tr("Unsubscribed from texts by office")
                      : tr("Unsubscribed from texts")}
                    {commPreferences.data?.smsOptedOutAt &&
                      ` — ${new Date(
                        commPreferences.data.smsOptedOutAt
                      ).toLocaleDateString()}`}
                    <button
                      type="button"
                      className="underline font-medium disabled:opacity-50"
                      disabled={resubscribe.isLoading}
                      onClick={() => resubscribe.mutate("sms")}
                    >
                      {tr("Resubscribe")}
                    </button>
                  </span>
                )}
                {commPreferences.data?.emailOptedOut && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs">
                    {commPreferences.data?.emailOptedOutBy
                      ? tr("Unsubscribed from emails by office")
                      : tr("Unsubscribed from emails")}
                    {commPreferences.data?.emailOptedOutAt &&
                      ` — ${new Date(
                        commPreferences.data.emailOptedOutAt
                      ).toLocaleDateString()}`}
                    <button
                      type="button"
                      className="underline font-medium disabled:opacity-50"
                      disabled={resubscribe.isLoading}
                      onClick={() => resubscribe.mutate("email")}
                    >
                      {tr("Resubscribe")}
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
          <div
            style={{
              margin: "auto",
              marginBottom: "0",
              marginLeft: "20px",
              color:
                customer.data?.info.total_amount_due === 0 ? "green" : "red",
              fontWeight: "bold",
              fontSize: "1.0rem",
              // light green background color
              backgroundColor:
                customer.data?.info.total_amount_due === 0
                  ? "#d4edda"
                  : "#f8d7da",
              padding: "5px",
              borderRadius: "5px 5px 0 0",
            }}
          >
            {tr("Total Amount Due")}:{" "}
            {dollarFormatter(customer.data?.info.total_amount_due)}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() =>
                modal.setModal({
                  component: <RequestModal customerId={params.id} />,
                  label: tr("New Request"),
                })
              }
              primary
            >
              {tr("Create Request")}
            </Button>
            <Dropdown
              reference={
                <Button>
                  <RiArrowDownSLine /> {tr("Action")}
                </Button>
              }
              data={[
                {
                  button: [
                    {
                      label: tr("Apply Credit"),
                      onClick: () =>
                        modal.setModal({
                          component: (
                            <NewPaymentModal
                              customerId={params.id}
                              id={params.id}
                              type="customer"
                              is_deposit
                            />
                          ),
                          label: tr("Apply Credit"),
                        }),
                    },
                    {
                      label: tr("Edit"),
                      onClick: () =>
                        history.push(`/customers/edit/${params.id}`),
                    },
                    {
                      // Offered only while there is something to stop. Both
                      // channels already off means the action would be a no-op,
                      // and an action that does nothing invites the belief that
                      // it failed.
                      label: tr("Unsubscribe from marketing"),
                      disabled:
                        optOut.isLoading ||
                        (!!commPreferences.data?.emailOptedOut &&
                          !!commPreferences.data?.smsOptedOut),
                      tooltip:
                        !!commPreferences.data?.emailOptedOut &&
                        !!commPreferences.data?.smsOptedOut
                          ? tr("This customer is already unsubscribed from email and texts.")
                          : tr("Stops marketing campaigns only. Invoices, estimates and appointment emails still send."),
                      onClick: () => optOut.mutate("all"),
                    },
                    {
                      label: (
                        <span className="flex space-x-2 items-center !text-red-500">
                          <RiDeleteBin7Line />
                          <span>{tr("Delete")}</span>
                        </span>
                      ),
                      onClick: () => activate(() => deleteCustomer.mutate()),
                    },
                  ],
                },
              ]}
            />
          </div>
        </div>
      </SafeArea>
      <SafeArea>
        <Tab data={data} defaultTab={"overview"} />
      </SafeArea>
    </div>
  );
}

const CustomerInfoTab = ({ data }: { data?: TCustomer }) => {
  const { t } = useTranslation();
  const tBase = "views.customer.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  
  const modal = useModalContext();

  const propertyQuery = useQuery<TProperty[]>(
    ["property", "customer", data?.id],
    async () =>
      await axios
        .get(
          `
        ${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${data?.id}/property
        `
        )
        .then((res) => res.data)
  );

  const customerCredit = useQuery<number | null>(
    ["customer credit", data?.id],
    async () =>
      data?.id
        ? await axios
            .get(
              `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${data?.id}/credit`
            )
            .then((res) => res.data)
        : null
  );

  const sortedProperties = useMemo(() => {
    const list = propertyQuery.data || [];
    const bid = data?.fk_billing_address_id;
    if (bid == null) return list;
    return [...list].sort((a, b) => {
      const aFirst = String(a.id) === String(bid) ? 0 : 1;
      const bFirst = String(b.id) === String(bid) ? 0 : 1;
      return aFirst - bFirst;
    });
  }, [propertyQuery.data, data?.fk_billing_address_id]);

  const populateAddresses = (sortedProperties.length > 0 &&
    sortedProperties.map((property) => (
      <PropertyCon
        property={property}
        key={property.id}
        customerId={data?.id}
        isDefaultBilling={
          data?.fk_billing_address_id != null &&
          String(property.id) === String(data.fk_billing_address_id)
        }
        defaultBillingLabel={tr("Default billing")}
      />
    ))) || <div className="text-slate-500">{tr("No Address")}</div>;

  const populateContacts = (data &&
    data.contacts?.map((contact, i) => {
      const extraForContact = (data.extended_contacts || []).filter(
        (e) =>
          e.fk_business_customer_contact_id === contact.id &&
          (e.contact_type === "email" || e.contact_type === "phone")
      );
      return (
        <SContact key={contact.id ?? i}>
          <div>
            <h3>{contact.name}</h3>
          </div>
          <div>
            <span>
              {`${contact.email} • ${contact.phone}`}
              {contact.extension && <span> *{contact.extension}</span>}
              {contact.title && <span> • {contact.title}</span>}
            </span>
          </div>
          {extraForContact.length > 0 && (
            <div className="mt-2">
              <span
                className="text-xs font-medium text-slate-500"
                style={{ color: theme.color.subtext3 }}
              >
                {tr("Additional emails and phones")}
              </span>
              <ContactInfoDisplay
                entries={extraForContact}
                billing
                notification
              />
            </div>
          )}
        </SContact>
      );
    })) || <div className="text-slate-500">{tr("No Additional Contact")}</div>;

  return (
    <div>
      {customerCredit.data !== 0 && (
        <div className="flex justify-between p-2 text-green-900 bg-green-100 rounded">
          <div className="flex flex-col">
            <span className="text-xs">{tr("Customer Credit")}</span>
            <span className="text-lg">
              {dollarFormatter(customerCredit.data)}
            </span>
          </div>
        </div>
      )}
      <FlatContainer
        title={tr("Service Properties")}
        buttons={[
          <OutlineButton
            primary
            onClick={() =>
              modal.setModal({
                component: (
                  <NewPropertyModal customerId={data?.id} customerInfo={data} />
                ),
                label: tr("New Property"),
              })
            }
          >
            <RiAddLine /> {tr("Add Property")}
          </OutlineButton>,
        ]}
      >
        <div className="divide-y">{populateAddresses}</div>
      </FlatContainer>
      <FlatContainer title={tr("Additional Contacts")}>
        {Array.isArray(data?.contacts) && data.contacts.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {populateContacts}
          </div>
        ) : (
          populateContacts
        )}
      </FlatContainer>
    </div>
  );
};

const SContact = styled.div`
  border-bottom: 1px solid ${theme.border.light};
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  > div:last-child {
    color: ${theme.color.subtext3};
  }
`;
